/**
 * activitiesViewModel — `(reduxState) → props` de l'onglet « Activités » du parent (patron A).
 *
 * Ce que le legacy lit, dans le même ordre (`TodoListScreen.js:158-160`, `:213-216`) :
 * - l'élève est résolu dans `currentUser.students` par son id, avec `currentUser` en repli —
 *   c'est `StudentTabsNavigator.js:46-48` suivi de `const user = student ? student : props.user`.
 *   En mode parent le jeton EST celui de l'enfant, donc les deux pointent le même élève ; le
 *   réducteur de `getStudent` ne remplit que `students[]` (`currentUser.js:955-1055`), d'où le repli.
 * - « À faire » = `user.todo_activities`, dans l'ordre servi par le backend (`todo_index`) : la
 *   liste legacy est glissable, elle ne trie pas.
 * - « Faites » = `getTodaysFinishedActivities(user)`, recopié ci-dessous.
 */
import moment from "moment"
import { createSelector } from "reselect"

import { toUIActivities, type ApiActivity } from "../../../adapters/parent/Activities/activityAdapter"
import { type UIActivity } from "../../../ui/types"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

export type ActivitiesViewModel = {
    /** Les activités à faire. */
    activities: UIActivity[]
    /** Les activités récemment faites — cinq au plus. */
    doneActivities: UIActivity[]
    /** Premier chargement (rien à montrer encore). */
    loading: boolean
    /** Rafraîchissement alors que la liste est déjà à l'écran. */
    refreshing: boolean
    /** Erreur bloquante — seulement quand il n'y a rien à montrer. */
    error: unknown | null
}

/**
 * Port littéral de `getTodaysFinishedActivities` (`data/utility/dataUtils.js:534-549`) : celles du
 * jour, les plus récentes en tête, puis les deux dernières d'avant, le tout borné à cinq.
 */
export function recentFinishedActivities(activities: ApiActivity[] | null | undefined): ApiActivity[] {
    const byMostRecent = (a: ApiActivity, b: ApiActivity) => moment(b.finished_at).diff(moment(a.finished_at))
    const finishedToday = (activities ?? [])
        .filter((a) => moment(a?.finished_at).isSame(moment(), "day"))
        .sort(byMostRecent)
    const before = (activities ?? [])
        .filter((a) => !moment(a?.finished_at).isSame(moment(), "day"))
        .sort(byMostRecent)
        .slice(0, 2)

    return finishedToday.concat(before).slice(0, 5)
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

export const activitiesViewModel = createSelector(
    [
        (state: ReduxState) => selectDisplayedStudent(state),
        (state: ReduxState) => state?.data?.staticData?.school_subjects,
        (state: ReduxState) => state?.api?.getStudent,
    ],
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    (currentUser: any, subjects: any, req: any): ActivitiesViewModel => {
        const student = currentUser?.students?.find(
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
            (candidate: any) => String(candidate?.id) === String(currentUser?.id)
        )
        const user = student ?? currentUser
        const requesting = !!req?.request

        const activities = toUIActivities(user?.todo_activities, subjects ?? [])
        const doneActivities = toUIActivities(recentFinishedActivities(user?.activities), subjects ?? [])
        const hasRows = activities.length > 0 || doneActivities.length > 0

        return {
            activities,
            doneActivities,
            loading: requesting && !hasRows,
            refreshing: requesting && hasRows,
            error: hasRows ? null : (req?.error ?? null),
        }
    }
)
